import { AttachInternals, Component, Element, Event, EventEmitter, h, Host, Method, Prop, Watch } from '@stencil/core'

import Helpers from '../../utils/helpers'
import { CssClassMap } from '../../utils/interfaces'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/modal?tab=designer).
 *
 * @slot header - Cabeçalho do modal. Se não for usado, o `title-text` será exibido.
 * @slot default - Corpo do modal.
 * @slot footer - Rodapé do modal.
 * @slot close-button - Botão de fechar customizado. Se não for usado, o botão padrão será exibido.
 *
 */
@Component({
  tag: 'br-modal',
  styleUrl: 'modal.scss',
  shadow: true,
  formAssociated: true,
})
export class Modal {
  /** Referência ao elemento host do componente. */
  @Element() el: HTMLBrModalElement

  @AttachInternals() elementInternals: ElementInternals

  /**
   * Identificador único.
   * Caso não seja fornecido, um ID gerado automaticamente será usado.
   */
  @Prop({ reflect: true }) readonly customId: string = Helpers.generateUniqueId('modal')

  /** O texto do título a ser exibido no cabeçalho do modal. Usado quando o slot `header` não é fornecido. */
  @Prop() readonly titleText: string

  /** Controla a visibilidade do modal. */
  @Prop({ reflect: true, mutable: true }) show = false

  /**
   * Define o comportamento de fechamento do modal.
   *
   * - `true`: O modal fecha automaticamente ao clicar no botão fechar.
   * - `false`: O modal emite `brModalBeforeClose` mas não fecha, permitindo a implementação de lógica customizada (validação, confirmação, etc.) antes do fechamento. O desenvolvedor deve
   *   controlar manualmente o fechamento.
   */
  @Prop({ reflect: true }) autoClose = false

  /**
   * Seletor CSS do elemento que deve receber foco quando o modal é aberto (ex: `"#meu-elemento"`).
   */
  @Prop() readonly initialFocusSelector: string

  /**
   * Define o tamanho (largura) do modal.
   */
  @Prop({ reflect: true }) readonly size: 'xsmall' | 'small' | 'medium' | 'large' | 'auto' = 'medium'

  /**
   * Se `true`, habilita a rolagem interna do conteúdo do modal.
   */
  @Prop({ reflect: true }) readonly scrollable = false

  /**
   * Define o alinhamento do conteúdo do rodapé (slot="footer").
   */
  @Prop({ reflect: true }) readonly alignFooter: 'start' | 'end' | 'center' = 'center'

  private isOpeningParentScrim = false
  private triggerElement: HTMLElement | null = null
  private pendingFocus = false

  private static readonly FOCUSABLE_SELECTORS = [
    'a[href]:not([disabled])',
    'button:not([disabled])',
    'br-button:not([disabled]):not([tabindex="-1"])',
    'input:not([disabled])',
    'br-input:not([disabled]):not([tabindex="-1"])',
    'select:not([disabled])',
    'br-select:not([disabled]):not([tabindex="-1"])',
    'textarea:not([disabled])',
    'br-textarea:not([disabled]):not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',')

  /**
   * Evento emitido quando o modal é aberto (quando `show` muda de `false` para `true`).
   */
  @Event() brModalOpen: EventEmitter<void>

  /**
   * Evento emitido após o modal ser fechado (quando `show` muda de `true` para `false`).
   */
  @Event() brModalClose: EventEmitter<void>

  /**
   * Evento emitido antes do fechamento do modal (quando o botão X é clicado).
   * Se autoClose está desativado, o desenvolvedor deve fechar manualmente o modal após este evento.
   * Se autoClose está ativado, o modal fecha automaticamente após este evento.
   */
  @Event() brModalBeforeClose: EventEmitter<void>

  /**
   * Evento emitido após o modal estar completamente aberto e com o foco estabilizado
   * dentro dele. Complementa `brModalOpen` (que dispara imediatamente ao abrir):
   * use `brModalOpened` quando precisar interagir com o modal já pronto.
   */
  @Event({ bubbles: true, composed: true }) brModalOpened: EventEmitter<void>

  @Watch('show')
  handleShowChange(newValue: boolean, oldValue: boolean) {
    if (newValue) {
      this.triggerElement = document.activeElement as HTMLElement
      this.brModalOpen.emit()
      this.openParentScrim()
      requestAnimationFrame(() => this.moveFocusIntoModal())
    } else if (oldValue === true) {
      this.brModalClose.emit()
      this.closeParentScrim()
      this.triggerElement?.focus()
      this.triggerElement = null
    }
  }

  componentWillLoad() {
    if (!this.show) {
      const parentScrim = this.el.closest('br-scrim')
      if (parentScrim !== null && parentScrim.isOpen) {
        this.show = true
      }
    }
  }

  componentDidLoad() {
    this.listenToParentScrimEvents()

    if (this.show) {
      this.openParentScrim()
      requestAnimationFrame(() => this.moveFocusIntoModal())
    }
    document.addEventListener('keydown', this.handleKeyDown, true)
  }

  disconnectedCallback() {
    this.isOpeningParentScrim = false
    this.removeParentScrimEventListeners()
    document.removeEventListener('keydown', this.handleKeyDown, true)
  }

  componentDidRender() {
    if (this.pendingFocus && this.show) {
      this.pendingFocus = false
      this.moveFocusIntoModal()
    }
  }

  /**
   * GERENCIAMENTO DE FOCO
   *
   * Métodos responsáveis por mover o foco para dentro do modal, estabilizar o
   * foco em elementos iniciais e localizar elementos nativos focáveis dentro
   * de web components que utilizam Shadow DOM.
   */

  private async moveFocusIntoModal() {
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))

    if (!this.show) return

    const root = this.el.shadowRoot
    if (root === null) return

    const modal = root.querySelector<HTMLElement>('.br-modal')
    if (modal !== null) {
      modal.focus()
      modal.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    if (typeof this.initialFocusSelector === 'string' && this.initialFocusSelector !== '') {
      const target = await this.waitForElement(this.initialFocusSelector)

      if (target !== null) {
        const focusTarget = this.findDeepNativeFocusable(target) ?? target
        focusTarget.focus()
        target.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }

    this.brModalOpened.emit()
  }

  /**
   * Encontra recursivamente o primeiro elemento nativo focusável dentro de um elemento,
   * perfurando shadow roots aninhados em qualquer profundidade (ex: br-select → br-input → <input>).
   *
   * @returns O elemento nativo encontrado, ou null se nenhum for encontrado.
   */
  private findDeepNativeFocusable(el: HTMLElement): HTMLElement | null {
    const root = el.shadowRoot
    if (root === null) return null

    // Busca direta no shadow imediato, por ordem de prioridade
    const prioritySelectors = [
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'button:not([disabled])',
      'a[href]',
    ]
    for (const sel of prioritySelectors) {
      const found = root.querySelector<HTMLElement>(sel)
      if (found !== null) return found
    }

    // Nenhum nativo direto — procurar em custom elements filhos que também tenham shadow
    // (ex: br-select.shadow → br-input → br-input.shadow → <input>)
    const customChildren = Array.from(root.querySelectorAll<HTMLElement>('*')).filter(
      (child) => child.tagName.includes('-') && child.shadowRoot !== null
    )
    for (const child of customChildren) {
      const inner = this.findDeepNativeFocusable(child)
      if (inner !== null) return inner
    }

    return null
  }

  /**
   * Aguarda o elemento aparecer no DOM e estar pronto para receber foco.
   * Tenta por até 10 vezes com intervalos de 50ms (500ms total).
   */
  private waitForElement(selector: string, retries = 10, interval = 50): Promise<HTMLElement | null> {
    return new Promise((resolve) => {
      const attempt = (remaining: number) => {
        const el = document.querySelector<HTMLElement>(selector)

        if (el !== null) {
          // Se for custom element, aguarda upgrade antes de resolver
          const tag = el.tagName.toLowerCase()
          if (tag.includes('-')) {
            customElements.whenDefined(tag).then(() => resolve(el))
          } else {
            resolve(el)
          }
          return
        }

        if (remaining <= 0) {
          resolve(null)
          return
        }

        setTimeout(() => attempt(remaining - 1), interval)
      }

      attempt(retries)
    })
  }

  /**
   * TECLADO
   *
   * Manipuladores e utilitários relacionados à interação por teclado,
   * incluindo tratamento de `Escape` para fechar o modal e navegação por
   * `Tab`/`Shift+Tab` para manter o foco preso (focus trap) dentro do modal.
   */

  private handleKeyDown = (event: KeyboardEvent) => {
    if (!this.show) return

    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      this.brModalBeforeClose.emit()
      this.show = false
      return
    }

    if (event.key === 'Tab') {
      this.handleTabNavigation(event)
    }
  }

  /**
   * Implementa o focus trap circular dentro da modal.
   * Quando Tab é pressionado no último elemento focalizável, o foco vai para o primeiro.
   * Quando Shift+Tab é pressionado no primeiro, o foco vai para o último.
   *
   * Usa `composedPath()[0]` para obter o elemento real com foco, pois web components
   * com shadow DOM retornam o host via `document.activeElement`, não o elemento nativo interno.
   *
   * Usa `findDeepNativeFocusable` no wrap porque `host.focus()` é no-op em web components
   * sem `delegatesFocus` — focar o elemento nativo mais profundo garante que o wrap aconteça.
   */
  private handleTabNavigation(event: KeyboardEvent) {
    const focusable = this.getFocusableElements()
    if (focusable.length === 0) {
      event.preventDefault()
      return
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const activeEl = (event.composedPath?.()[0] ?? document.activeElement) as HTMLElement

    const firstTarget = this.findDeepNativeFocusable(first) ?? first
    const lastTarget = this.findDeepNativeFocusable(last) ?? last

    if (!event.shiftKey && this.isOrInsideElement(activeEl, last)) {
      event.preventDefault()
      firstTarget.focus()
    } else if (event.shiftKey && this.isOrInsideElement(activeEl, first)) {
      event.preventDefault()
      lastTarget.focus()
    }
  }

  /**
   * Verifica se `target` é igual a `container` ou está dentro dele,
   * perfurando shadow roots na subida (getRootNode().host).
   */
  private isOrInsideElement(target: HTMLElement, container: HTMLElement): boolean {
    if (target === container) return true
    let node: Node = target
    while (node !== null && node !== document) {
      if (node === container) return true
      const root = node.getRootNode()
      if (root instanceof ShadowRoot) {
        node = root.host
      } else {
        node = (node as HTMLElement).parentElement
        if (node === null) break
      }
    }
    return false
  }

  /**
   * Coleta todos os elementos focalizáveis dentro da modal,
   * perfurando o shadow DOM: inclui elementos do shadow root (ex: botão fechar)
   * e dos slots (conteúdo fornecido pelo usuário nos slots header, default, footer e close-button).
   */
  private getFocusableElements(): HTMLElement[] {
    const root = this.el.shadowRoot
    if (root === null) return []

    const selector = Modal.FOCUSABLE_SELECTORS
    const elements: HTMLElement[] = []

    // Elementos focalizáveis diretamente no shadow root (ex: br-button.close)
    root.querySelectorAll<HTMLElement>(selector).forEach((el) => elements.push(el))

    // Elementos dos slots (conteúdo do usuário)
    root.querySelectorAll<HTMLSlotElement>('slot').forEach((slot) => {
      slot.assignedElements({ flatten: true }).forEach((assigned) => {
        if (!(assigned instanceof HTMLElement)) return
        if (assigned.matches(selector)) elements.push(assigned)
        assigned.querySelectorAll<HTMLElement>(selector).forEach((el) => elements.push(el))
      })
    })

    // Remove referências duplicadas e filtra elementos não visíveis.
    // Retorna apenas elementos realmente focáveis e perceptíveis ao usuário.
    return Array.from(new Set(elements)).filter(
      (el) => el.offsetWidth > 0 && el.offsetHeight > 0 && getComputedStyle(el).visibility !== 'hidden'
    )
  }

  /**
   * INTEGRAÇÃO COM BR-SCRIM
   *
   * Métodos para integrar o comportamento do modal com um `br-scrim` pai,
   * incluindo abertura/fechamento sincronizados e listeners para eventos do
   * scrim.
   */

  private closeParentScrim() {
    const parentScrim = this.el.closest('br-scrim')
    if (parentScrim && typeof parentScrim.close === 'function') {
      parentScrim.close()
    }
  }

  /**
   * Adiciona listeners para eventos do parent scrim
   */
  private listenToParentScrimEvents() {
    const parentScrim = this.el.closest('br-scrim')
    if (parentScrim !== null) {
      parentScrim.addEventListener('brScrimOpen', this.handleParentScrimOpen)
      parentScrim.addEventListener('brScrimClose', this.handleParentScrimClose)
    }
  }

  /**
   * Remove listeners de eventos do parent scrim
   */
  private removeParentScrimEventListeners() {
    const parentScrim = this.el.closest('br-scrim')
    if (parentScrim !== null) {
      parentScrim.removeEventListener('brScrimOpen', this.handleParentScrimOpen)
      parentScrim.removeEventListener('brScrimClose', this.handleParentScrimClose)
    }
  }

  /**
   * Handler para evento brScrimOpen do parent scrim
   * Quando scrim abre programaticamente, a modal deve abrir também
   */
  private handleParentScrimOpen = () => {
    const parentScrim = this.el.closest('br-scrim')
    if (parentScrim === null) return

    if (!this.show) {
      this.show = true
    }
  }

  /**
   * Handler para evento brScrimClose do parent scrim
   * Se autoClose=true, fecha a modal junto com o scrim
   * Se autoClose=false, não faz nada (modal permanece aberta)
   */
  private handleParentScrimClose = () => {
    const parentScrim = this.el.closest('br-scrim')
    if (parentScrim === null) return

    if (this.autoClose && this.show) {
      this.show = false
    }
  }

  private openParentScrim() {
    if (this.isOpeningParentScrim) return

    const parentScrim = this.el.closest('br-scrim')
    if (parentScrim && !parentScrim.isOpen) {
      this.isOpeningParentScrim = true

      Promise.resolve()
        .then(() => {
          try {
            return parentScrim.open()
          } catch (error) {
            console.warn('br-modal: Erro ao abrir scrim pai:', error)
            throw error
          }
        })
        .finally(() => {
          this.isOpeningParentScrim = false
        })
    }
  }

  /**
   * UTILITÁRIOS
   *
   * Getters e helpers utilitários usados internamente pelo componente,
   * como geração de IDs e mapeamento de classes CSS.
   */

  private get titleId() {
    return `${this.customId}-title`
  }

  private get contentId() {
    return `${this.customId}-content`
  }

  private getCssClassMap(): CssClassMap {
    return {
      'br-modal': true,
      [this.size]: true,
      scrollable: this.scrollable,
    }
  }

  private handleClose = () => {
    this.brModalBeforeClose.emit()

    if (this.autoClose) {
      this.show = false
    }
  }

  /**
   * MÉTODOS PÚBLICOS (API)
   *
   * Métodos públicos expostos pela API do componente (`open`, `close`, etc.).
   */

  /**
   * Método público para abrir o modal
   */
  @Method()
  async open() {
    this.show = true

    this.openParentScrim()
  }

  /**
   * Método público para fechar o modal
   */
  @Method()
  async close() {
    this.show = false
  }

  render() {
    if (!this.show) {
      return null
    }

    return (
      <Host>
        <div
          class={this.getCssClassMap()}
          role="dialog"
          tabIndex={-1}
          aria-modal="true"
          aria-labelledby={this.titleId}
          aria-describedby={this.contentId}
        >
          <div class="br-modal-header">
            <div class="modal-title" id={this.titleId}>
              <slot name="header">{this.titleText}</slot>
            </div>
            <slot name="close-button">
              <br-button class="close" shape="circle" density="small" aria-label="Fechar" onClick={this.handleClose}>
                <br-icon icon-name="fa-solid:times"></br-icon>
              </br-button>
            </slot>
          </div>
          <div id={this.contentId} class="br-modal-body">
            <slot></slot>
          </div>
          <div class={`br-modal-footer ${this.alignFooter !== undefined ? `justify-content-${this.alignFooter}` : ''}`}>
            <slot name="footer"></slot>
          </div>
        </div>
      </Host>
    )
  }
}
